import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import JumlatyLogo from "../components/JumlatyLogo";
import CustomerServiceModal from "../components/CustomerServiceModal";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Phone must be exactly 11 digits (Iraqi format).
    // Requested Feb 27, 2026 — block typos before hitting the server.
    if (!/^\d{11}$/.test(phone.trim())) {
      setError("تأكد من رقم الهاتف — يجب أن يكون 11 رقم بالضبط");
      return;
    }
    setLoading(true);
    try {
      const user = await login(phone, password);
      toast.success(`أهلاً ${user.full_name}!`);
      if (user.role === "admin") {
        navigate("/admin");
      } else if (user.role === "driver") {
        navigate("/sawakeen-jumlaty");
      } else if (user.role === "mujahez") {
        navigate("/mujahez-jumlaty");
      } else if (user.role === "mandoob") {
        navigate("/mandoob");
      } else if (user.role === "supervisor" || user.role === "rep") {
        navigate("/mushrif-jumlaty");
      } else {
        navigate("/");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      setError(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <JumlatyLogo size="xl" showTagline tagline="التجارة الذكية في العراق" />
          </Link>
          <p className="text-[#1E3A5F] font-medium mt-3">تسجيل الدخول إلى حسابك</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-white/85 backdrop-blur-md border border-[#BAE6FD] rounded-3xl p-6 sm:p-8 shadow-xl">
          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              رقم الموبايل
            </label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={11}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
              data-testid="login-phone"
              className={`w-full px-4 py-3 rounded-2xl border focus:outline-none focus:ring-2 bg-white ${
                phone.length > 0 && phone.length !== 11
                  ? "border-[#E85D45] ring-1 ring-[#E85D45]/40 focus:ring-[#E85D45]"
                  : "border-[#E4EBE6] focus:ring-[#2D4A3E]"
              }`}
              placeholder="07XXXXXXXXX"
              required
            />
            {phone.length > 0 && phone.length !== 11 && (
              <p
                data-testid="login-phone-hint"
                className="mt-1.5 text-[12px] font-bold text-[#E85D45] flex items-center gap-1"
              >
                ⚠️ تأكد من رقم الهاتف — يجب أن يكون 11 رقم بالضبط ({phone.length}/11)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              كلمة السر
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
              placeholder="••••••"
              required
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-[#E85D45] text-sm">
              {error}
            </p>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
            data-testid="login-submit"
            className="w-full bg-[#2D4A3E] hover:bg-[#233A30] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50"
          >
            {loading ? "جاري الدخول..." : "تسجيل الدخول"}
          </motion.button>

          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            data-testid="forgot-password-link"
            className="block mx-auto text-center text-sm text-[#2D4A3E] hover:text-[#1ebd5b] font-medium underline-offset-4 hover:underline transition-colors"
          >
            هل نسيت كلمة المرور؟
          </button>

          <p className="text-center text-sm text-[#5C6E64]">
            ليس لديك حساب؟{" "}
            <Link
              to="/signup"
              data-testid="goto-signup"
              className="text-[#E85D45] font-medium hover:underline"
            >
              أنشئ حساب جديد
            </Link>
          </p>
        </form>
      </motion.div>

      <CustomerServiceModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
};

export default Login;
